import { clientCapabilities } from './native.ts';
import type { LibrarySort } from './sort.ts';

export interface LibrarySection {
  key: string;
  title: string;
  type: string;
  /** What an admin set for everyone, or null for "nobody has said". */
  defaultSort: string | null;
  defaultDesc: boolean;
}

/** One row of the admin screen. Browser only — the TV app never asks. */
export interface AdminUser {
  id: string;
  displayName: string;
  username: string;
  /** False when Plex revoked the token; they will be asked to link again. */
  linked: boolean;
  createdAt: string;
  can: CurrentUser['can'];
  /**
   * The address this profile signs in to Seerr with, or null.
   *
   * The password is never sent back — there is no screen that needs to show
   * it and every copy is somewhere it can leak from.
   */
  seerrEmail: string | null;
  /** A managed Plex Home profile: Seerr cannot sign it in via Plex at all. */
  managed: boolean;
}
import type {
  Channel, ChannelGroup, CurrentUser, Guide, HomeProfile, LibraryItem, Program, PublicProfile,
  CollectionDetails, LibraryItemDetail, RequestableDetails, RequestCard,
  SearchResults, StreamDescriptor, SxmBusyError, PlaybackTracks,
  AdminChannel, AdminProvider, AdminRule, EpgStatus, ImportPreview, ImportResult,
  ProviderGroup, RuleRunResult, ServerInfo, SyncRuleWire,
  GameEntry, GameSystem,
} from '@shared/types.ts';

/**
 * What a provider form sends.
 *
 * `password` is WRITE-ONLY and absent means "leave the stored one alone" —
 * the server reads it exactly that way. There is no field for reading one
 * back because there is no screen that needs it; `hasSecret` on AdminProvider
 * is the only thing ever said about a stored credential.
 */
export interface ProviderInput {
  kind?: 'xtream' | 'm3u' | 'iheart' | 'sxm' | 'custom';
  name?: string;
  enabled?: boolean;
  host?: string;
  username?: string;
  password?: string;
  playlistUrl?: string;
  epgUrl?: string;
}

/** What an import asks for. `preview` makes it answer without acting. */
export interface ImportInput {
  groups?: string[];
  search?: string;
  preview?: boolean;
  numberStart?: number;
  numberEnd?: number;
  /** Past the 500 ceiling the server refuses until this says go ahead. */
  confirmLarge?: boolean;
}

/** What a rule form sends. */
export interface RuleInput {
  label?: string;
  providerId?: string;
  categoryId?: string;
  categoryName?: string;
  numberRangeStart?: number;
  numberRangeEnd?: number;
  enabled?: boolean;
}

/** What a hand-added channel needs. Everything past `name` is per-source. */
export interface ChannelInput {
  type: string;
  name: string;
  number?: number;
  category?: string;
  logo?: string | null;
  kind?: 'tv' | 'radio';
  enabled?: boolean;
  streamId?: string;
  streamUrl?: string;
  epgId?: string;
  iheartStationId?: string;
  sxmChannelId?: string;
  customUrl?: string;
}

/**
 * Typed wrappers over the API. Same-origin throughout, so cookies just work
 * and there is no base URL to configure.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly body: any;
  constructor(status: number, body: any) {
    super(body?.message ?? body?.error ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * A 409 from the stream endpoint is not really an error — it is the server
 * telling us SiriusXM is already playing something else, with enough detail
 * to offer "listen to that instead". Give it its own type so the tune flow
 * can branch on it rather than route it to a generic error boundary.
 */
export class SourceBusyError extends ApiError {
  readonly active: SxmBusyError['active'];
  constructor(body: SxmBusyError) {
    super(409, body);
    this.name = 'SourceBusyError';
    this.active = body.active;
  }
}

/**
 * The server could not be reached at all — as opposed to answering with an
 * error. Worth its own type: "wrong password" and "the server is down" are
 * completely different problems, and collapsing them into one message sends
 * you hunting in the wrong place. (It did exactly that once already.)
 */
export class NetworkError extends Error {
  constructor(message = 'Could not reach the server') {
    super(message);
    this.name = 'NetworkError';
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: 'same-origin',
      headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
      ...init,
    });
  } catch {
    // fetch only rejects when the request never got an HTTP response:
    // connection refused, DNS failure, offline.
    throw new NetworkError();
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const body = text ? safeJson(text) : null;

  if (!res.ok) {
    if (res.status === 409 && body?.error === 'sxm_busy') throw new SourceBusyError(body);
    // Vite's proxy answers 500 with an HTML error page when the API is down.
    // That is an unreachable server, not an API response.
    if (res.status >= 500 && !body?.error) throw new NetworkError();
    throw new ApiError(res.status, body);
  }
  return body as T;
}

function safeJson(text: string): any {
  try { return JSON.parse(text); } catch { return { message: text.slice(0, 200) }; }
}

export const api = {
  // ── auth ──
  profiles: () =>
    req<{ profiles: PublicProfile[]; setupRequired: boolean }>('/api/auth/profiles'),

  me: () => req<{ user: CurrentUser }>('/api/auth/me'),

  /** Ask plex.tv for a four-character code to put on screen. */
  plexStart: () =>
    req<{ id: number; code: string; expiresAt: number; qrUrl?: string; qrImage?: string }>('/api/auth/plex/start', { method: 'POST' }),

  /**
   * Has anybody claimed it yet? `pending: true` is the ordinary answer and is
   * not an error — this gets asked every couple of seconds while somebody
   * finds their phone.
   */
  plexPoll: (id: number) =>
    req<{ pending: boolean; user?: CurrentUser }>(`/api/auth/plex/poll?id=${id}`),

  login: (creds: { userId?: string; username?: string; password?: string; pin?: string }) =>
    req<{ user: CurrentUser }>('/api/auth/login', {
      method: 'POST', body: JSON.stringify(creds),
    }),

  logout: () => req<{ ok: true }>('/api/auth/logout', { method: 'POST' }),

  /**
   * The profiles this sign-in may switch into. Usually none.
   *
   * Only the owner of a Plex Home gets any, and only the managed profiles
   * inside it — everybody with a Plex account of their own signs in with it.
   * The server decides all of that; this just draws what comes back.
   */
  home: () =>
    req<{ profiles: HomeProfile[]; current: string; root: string }>('/api/auth/home'),

  switchProfile: (userId: string, pin?: string) =>
    req<{ user: CurrentUser }>('/api/auth/switch', {
      method: 'POST', body: JSON.stringify({ userId, pin }),
    }),

  /** Back to the account that did the switching. No PIN, no round trip to Plex. */
  switchBack: () =>
    req<{ user: CurrentUser }>('/api/auth/switch/back', { method: 'POST' }),

  // ── channels ──
  channels: (params: {
    kind?: 'tv' | 'radio';
    group?: string;
    q?: string;
    /** Only channels this viewer has starred. */
    favourites?: boolean;
    /** ONLY the hidden ones — the undo for hiding. */
    hidden?: boolean;
    /** Hidden ones alongside the rest. */
    includeHidden?: boolean;
  } = {}) => {
    const qs = new URLSearchParams();
    if (params.kind) qs.set('kind', params.kind);
    if (params.group) qs.set('group', params.group);
    if (params.q) qs.set('q', params.q);
    if (params.favourites) qs.set('favourites', '1');
    if (params.hidden) qs.set('hidden', '1');
    if (params.includeHidden) qs.set('includeHidden', '1');
    return req<{ channels: Channel[] }>(`/api/channels${qs.toString() ? `?${qs}` : ''}`)
      .then((r) => r.channels);
  },

  groups: (kind: 'tv' | 'radio') =>
    req<{ groups: ChannelGroup[]; favourites: number; hidden: number }>(
      `/api/groups?kind=${kind}`,
    ),

  // ── admin (browser only) ──
  // ── games ──
  // The shelf only. Launching is a full page load of /games/player.html,
  // which fetches its own launch details — see server/public/games/player.js.
  games: () => req<{ systems: GameSystem[]; games: GameEntry[] }>('/api/games'),
  setGameFavorite: (id: string, on: boolean) =>
    req<{ favorite: boolean }>(`/api/games/${encodeURIComponent(id)}/favorite`, { method: on ? 'PUT' : 'DELETE' }),

  adminUsers: () =>
    req<{ users: AdminUser[] }>('/api/admin/users').then((r) => r.users),

  setCapabilities: (id: string, patch: Partial<CurrentUser['can']>) =>
    req<{ user: { id: string; displayName: string; can: CurrentUser['can'] } }>(
      `/api/admin/users/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(patch) },
    ),

  /** An empty address clears it and puts the profile back on the fallback. */
  setSeerrLogin: (id: string, seerrEmail: string, seerrPassword: string) =>
    req<{ user: unknown }>(
      `/api/admin/users/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify({ seerrEmail, seerrPassword }) },
    ),

  // ── library ──
  /**
   * EVERY ROW OF THE HOME SCREEN, in one request.
   *
   * This was typed `{ resume, recent }` long after the server had grown to
   * send six lists, so four of them arrived and were discarded — New Movies,
   * New Episodes, On Now and the requests all crossed the wire and were
   * thrown away by the type. Meanwhile the requests row made a SECOND call
   * for data it had already been handed.
   *
   * The server builds all of this from two upstream Plex calls. Asking again
   * per row is what this endpoint exists to prevent.
   */
  libraryHome: () =>
    req<{
      resume: LibraryItem[];
      newMovies: LibraryItem[];
      newEpisodes: LibraryItem[];
      recent: LibraryItem[];
      requests: RequestCard[];
      /** For "On Now": favorites if any are starred, else the first twelve. */
      channels: Channel[];
    }>('/api/library/home'),

  librarySections: () =>
    req<{ sections: LibrarySection[] }>('/api/library/sections'),

  /** Admin only. Sets the order a library opens in for everybody. */
  setSectionSort: (key: string, sort: LibrarySort, desc: boolean) =>
    req<{ sort: string; desc: boolean }>(
      `/api/library/sections/${encodeURIComponent(key)}/sort`,
      { method: 'PUT', body: JSON.stringify({ sort, desc }) },
    ),

  /**
   * One page of a section. `total` comes back on the first page only.
   *
   * Returns the envelope rather than just the items, because the grid needs
   * to know whether there is more — a library here has over five thousand
   * films, and the page that does not ask for the next one is a library with
   * 4,940 invisible films in it.
   */
  libraryBrowse: (
    section: string,
    opts: { offset?: number; limit?: number; sort?: LibrarySort; desc?: boolean } = {},
  ) => {
    const q = new URLSearchParams({
      section,
      offset: String(opts.offset ?? 0),
      limit: String(opts.limit ?? 60),
    });
    if (opts.sort) q.set('sort', opts.sort);
    if (opts.desc) q.set('desc', '1');
    return req<{ items: LibraryItem[]; total?: number; offset: number; limit: number }>(
      `/api/library/browse?${q}`,
    );
  },

  libraryItem: (id: string) =>
    req<{ item: LibraryItemDetail }>(`/api/library/items/${encodeURIComponent(id)}`).then((r) => r.item),

  libraryChildren: (id: string) =>
    req<{ items: LibraryItem[] }>(`/api/library/items/${encodeURIComponent(id)}/children`)
      .then((r) => r.items),

  /**
   * What to play. `can` is the same capability string live channels send —
   * one vocabulary for "what this device can decode", not two.
   */
  libraryPlayback: (id: string, select?: { audio?: number | null }) => {
    const q = new URLSearchParams({ can: clientCapabilities().join(',') });
    /*
     * ONLY SENT WHEN SOMETHING WAS ACTUALLY CHOSEN. An `audio=` the server
     * has to recognise as "the default one anyway" is a parameter that can
     * tip a file out of direct play by accident — the route treats an
     * unrecognised id as no choice, but not sending it at all is the version
     * that cannot go wrong.
     */
    if (select?.audio) q.set('audio', String(select.audio));
    return req<{
      descriptor: StreamDescriptor; durationMs: number; mode: string;
      /** The episode actually playing, which may not be what was pressed. */
      itemId: string;
      resumeMs: number;
    } & PlaybackTracks>(
      `/api/library/items/${encodeURIComponent(id)}/playback?${q}`,
    );
  },

  /**
   * Change an item's state in PLEX, as this viewer.
   *
   * `watched` / `unwatched` / `remove-from-continue` / `reset-progress`. One
   * route rather than four, because they are one idea — and four would be four
   * places to forget the permission check.
   */
  /**
   * Is there a newer television app on the server?
   *
   * Public, so it works on a TV that is not signed in — which is exactly the
   * television that most needs telling.
   */
  appVersion: () =>
    req<{
      available: boolean; buildId?: string; bytes?: number;
      modified?: string; versionCode?: number; url?: string;
    }>('/api/app/version'),

  /**
   * Remove something from the library, files and all.
   *
   * ADMIN ONLY, and the server answers 404 rather than 403 to anybody else —
   * a permission error confirms the thing exists. It also translates Plex's
   * ambiguous 401 into a sentence about the "Allow media deletion" setting,
   * which is the actual cause nine times out of ten.
   */
  deleteItem: (id: string) =>
    req<{ ok: true }>(`/api/library/items/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  itemAction: (id: string, action: string) =>
    req<{ ok: true }>(`/api/library/items/${encodeURIComponent(id)}/state`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),

  /**
   * A URL that saves the file instead of playing it.
   *
   * Asks the playback endpoint for a descriptor — which is what mints the
   * signed stream token — and then appends `download=1` to the SAME url. It
   * is one route and one permission check, not a second copy of both.
   *
   * Null when the descriptor is an HLS transcode: there is no single file to
   * hand over in that case, only a playlist and a few hundred segments that
   * exist for as long as the session does.
   */
  downloadUrl: async (id: string): Promise<string | null> => {
    const r = await api.libraryPlayback(id);
    if (r.descriptor.kind !== 'progressive') return null;
    return `${r.descriptor.url}&download=1`;
  },

  /**
   * Where somebody has got to.
   *
   * `keepalive` so a report sent as the page goes away still leaves — the
   * "stopped" one is the most valuable of the lot and the easiest to lose.
   */
  libraryProgress: (id: string, body: {
    state: 'playing' | 'paused' | 'stopped'; timeMs: number; durationMs: number; session?: string;
  }) =>
    req<{ ok: true }>(`/api/library/items/${encodeURIComponent(id)}/progress`, {
      method: 'POST', body: JSON.stringify(body), keepalive: true,
    } as any),

  /** Partial by design — favouriting must never un-hide. */
  setChannelPref: (id: string, patch: { favourite?: boolean; hidden?: boolean }) =>
    req<{ channel: Channel }>(`/api/channels/${id}/prefs`, {
      method: 'PATCH', body: JSON.stringify(patch),
    }).then((r) => r.channel),

  channel: (id: string) =>
    req<{ channel: Channel }>(`/api/channels/${id}`).then((r) => r.channel),

  epg: (id: string, from?: Date, to?: Date) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from.toISOString());
    if (to) qs.set('to', to.toISOString());
    return req<{ programs: Program[] }>(`/api/channels/${id}/epg?${qs}`)
      .then((r) => r.programs);
  },

  /** The whole guide grid in one request — see `Guide` in shared/types.ts. */
  guide: (from: Date, to: Date, kind: 'tv' | 'radio' = 'tv') =>
    req<Guide>(`/api/epg/grid?from=${from.toISOString()}&to=${to.toISOString()}&kind=${kind}`),

  /**
   * Throws SourceBusyError when SXM is playing a different channel.
   *
   * `can` is the one thing the client says about itself, and it is
   * deliberately about DECODING rather than identity — a device with real
   * decoders is offered the continuous MPEG-TS pipe, which starts in a single
   * round-trip instead of waiting on a segmenter. The response is the same
   * StreamDescriptor either way, so nothing downstream branches on it.
   */
  stream: (id: string) => {
    const can = clientCapabilities();
    const qs = can.length ? `?can=${can.join(',')}` : '';
    return req<StreamDescriptor>(`/api/stream/${id}${qs}`);
  },

  // ── library (Phase 3) ──
  latest: (type: 'movie' | 'series') =>
    req<{ items: LibraryItem[] }>(`/api/library/latest?type=${type}`).then((r) => r.items),
  resume: () => req<{ items: LibraryItem[] }>('/api/library/resume').then((r) => r.items),

  search: (q: string) => req<SearchResults>(`/api/search?q=${encodeURIComponent(q)}`),

  /** Enough to be sure it is the right thing before asking for it. */
  requestDetails: (mediaType: 'movie' | 'tv', id: number) =>
    req<{ item: RequestableDetails }>(`/api/request/${mediaType}/${id}`).then((r) => r.item),

  /** What the household has asked for lately. Empty rather than failing. */
  recentRequests: () =>
    req<{ requests: RequestCard[] }>('/api/requests/recent').then((r) => r.requests),

  /** Every film in a series, and what the library has of it. */
  collection: (id: number) =>
    req<{ collection: CollectionDetails }>(`/api/request/collection/${id}`).then((r) => r.collection),

  /**
   * Ask for a whole series. One call, however many films — the loop is on the
   * server so a television makes one round trip rather than nine.
   */
  requestCollection: (ids: number[]) =>
    req<{ asked: number; failed: number }>('/api/request/collection', {
      method: 'POST', body: JSON.stringify({ ids }),
    }),

  /** Ask Seerr for something the library does not have, as this viewer. */
  request: (id: number, mediaType: 'movie' | 'tv', seasons?: number[]) =>
    req<{ ok: true }>('/api/request', {
      method: 'POST', body: JSON.stringify({ id, mediaType, seasons }),
    }),

  /* ── admin: the lineup and what feeds it ──────────────────────────────────
     Browser only in practice, and the server refuses a non-admin whatever
     asks. Grouped here rather than in a second module because there is one
     `req` with one error vocabulary, and a second copy of it is a second set
     of rules about what a 409 means. */

  /** Read-only, and every field already known without reaching upstream. */
  adminServer: () => req<ServerInfo>('/api/admin/server'),

  // ── providers ──
  adminProviders: () =>
    req<{ providers: AdminProvider[] }>('/api/admin/providers').then((r) => r.providers),

  adminProviderCreate: (body: ProviderInput) =>
    req<{ provider: AdminProvider }>('/api/admin/providers', {
      method: 'POST', body: JSON.stringify(body),
    }).then((r) => r.provider),

  /** Omitting `password` keeps the stored one. Sending '' also keeps it. */
  adminProviderPatch: (id: string, body: ProviderInput) =>
    req<{ provider: AdminProvider }>(`/api/admin/providers/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }).then((r) => r.provider),

  /** 409 with `{ channels }` while anything still points at it. */
  adminProviderDelete: (id: string) =>
    req<{ deleted: string }>(`/api/admin/providers/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  adminProviderGroups: (id: string) =>
    req<{ groups: ProviderGroup[] }>(
      `/api/admin/providers/${encodeURIComponent(id)}/groups`,
    ).then((r) => r.groups),

  /**
   * Pull channels in. ADD-ONLY: it never deletes and never renumbers.
   *
   * Overloaded on `preview` at the type level so a caller that asks for a
   * preview cannot accidentally read `added` off the answer.
   */
  adminProviderImport: (id: string, body: ImportInput & { preview: true }) =>
    req<ImportPreview>(`/api/admin/providers/${encodeURIComponent(id)}/import`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  adminProviderImportRun: (id: string, body: ImportInput) =>
    req<ImportResult>(`/api/admin/providers/${encodeURIComponent(id)}/import`, {
      method: 'POST', body: JSON.stringify({ ...body, preview: false }),
    }),

  // ── the lineup ──
  /** DISABLED CHANNELS INCLUDED — that is why this is not `/api/channels`. */
  adminChannels: (opts: { kind?: 'tv' | 'radio'; q?: string } = {}) => {
    const qs = new URLSearchParams();
    if (opts.kind) qs.set('kind', opts.kind);
    if (opts.q) qs.set('q', opts.q);
    return req<{ channels: AdminChannel[] }>(
      `/api/admin/channels${qs.toString() ? `?${qs}` : ''}`,
    ).then((r) => r.channels);
  },

  adminChannelCreate: (body: ChannelInput) =>
    req<{ channel: AdminChannel }>('/api/admin/channels', {
      method: 'POST', body: JSON.stringify(body),
    }).then((r) => r.channel),

  /** A number already in use comes back 409 with whose it is. */
  adminChannelPatch: (id: string, patch: Partial<ChannelInput>) =>
    req<{ channel: AdminChannel }>(`/api/admin/channels/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(patch),
    }).then((r) => r.channel),

  adminChannelDelete: (id: string) =>
    req<{ deleted: string }>(`/api/admin/channels/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),

  /**
   * These two trade numbers. THE CLIENT NAMES BOTH, because "up" means a
   * different channel in a filtered view than in the whole lineup and the
   * server has no business knowing which list is on screen.
   */
  adminChannelSwap: (a: string, b: string) =>
    req<{ channels: AdminChannel[] }>('/api/admin/channels/swap', {
      method: 'POST', body: JSON.stringify({ a, b }),
    }).then((r) => r.channels),

  /** Reorders into the numbers these channels ALREADY hold, sorted ascending. */
  adminChannelRenumber: (ids: string[]) =>
    req<{ channels: AdminChannel[] }>('/api/admin/channels/renumber', {
      method: 'POST', body: JSON.stringify({ ids }),
    }).then((r) => r.channels),

  adminChannelsEnabled: (ids: string[], enabled: boolean) =>
    req<{ changed: number; channels: AdminChannel[] }>('/api/admin/channels/enabled', {
      method: 'POST', body: JSON.stringify({ ids, enabled }),
    }),

  // ── sync rules ──
  adminRules: () => req<{ rules: AdminRule[] }>('/api/admin/rules').then((r) => r.rules),

  /** 409 when the range overlaps another rule's, naming which. */
  adminRuleCreate: (body: RuleInput) =>
    req<{ rule: SyncRuleWire }>('/api/admin/rules', {
      method: 'POST', body: JSON.stringify(body),
    }).then((r) => r.rule),

  adminRulePatch: (id: string, body: RuleInput) =>
    req<{ rule: SyncRuleWire }>(`/api/admin/rules/${encodeURIComponent(id)}`, {
      method: 'PATCH', body: JSON.stringify(body),
    }).then((r) => r.rule),

  /** Keeps the channels and clears the stamp — see the route's comment. */
  adminRuleDelete: (id: string) =>
    req<{ deleted: string; released: number }>(
      `/api/admin/rules/${encodeURIComponent(id)}`, { method: 'DELETE' },
    ),

  /** `preview` answers with the same four numbers and changes nothing. */
  adminRuleRun: (id: string, preview: boolean) =>
    req<{ preview: boolean; result: RuleRunResult }>(
      `/api/admin/rules/${encodeURIComponent(id)}/run`,
      { method: 'POST', body: JSON.stringify({ preview }) },
    ),

  // ── the guide ──
  /** Reads memory only. Never fetches, so it cannot hang. */
  adminEpg: () => req<{ feeds: EpgStatus[] }>('/api/admin/epg').then((r) => r.feeds),

  /** Downloads the guide again. Can take most of a minute. */
  adminEpgRefresh: (providerId: string) =>
    req<{ feed: EpgStatus }>(
      `/api/admin/epg/${encodeURIComponent(providerId)}/refresh`, { method: 'POST' },
    ).then((r) => r.feed),
};
